import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, apiClient, fleetPath } from '@/lib/api-client';

/** One exit IP in a server's pool. Matches the live API shape (camelCase). */
export interface ExitIp {
  ip: string;
  mode: string | null;
  assignedAt: string | null;
}

const exitIpKey = (name: string): readonly unknown[] => ['fleet', 'exit-ips', name];

function serverExitIpsPath(name: string): string {
  return fleetPath(`/servers/${encodeURIComponent(name)}/exit-ips`);
}

/** GET /servers/:name/exit-ips — the server's exit-IP pool. */
export function useExitIps(name: string): UseQueryResult<ExitIp[], Error> {
  return useQuery({
    queryKey: exitIpKey(name),
    queryFn: ({ signal }) => apiClient.get<ExitIp[]>(serverExitIpsPath(name), signal),
    enabled: Boolean(name),
  });
}

/** POST /servers/:name/exit-ips — assign an IP. A 409 surfaces as an ApiError. */
export function useAssignExitIp(
  name: string,
): UseMutationResult<ExitIp, ApiError, { ip: string; mode?: string }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => apiClient.post<ExitIp>(serverExitIpsPath(name), body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: exitIpKey(name) });
    },
  });
}

/** DELETE /servers/:name/exit-ips/:ip — release an IP back to the pool. */
export function useReleaseExitIp(
  name: string,
): UseMutationResult<{ ok: unknown }, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ip) =>
      apiClient.delete<{ ok: unknown }>(
        `${serverExitIpsPath(name)}/${encodeURIComponent(ip)}`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: exitIpKey(name) });
    },
  });
}
